import { ApiResponse as ApiSauceResponse } from 'apisauce';
import { AxiosRequestConfig, AxiosError } from 'axios';
import qs from 'qs';
import { redirectToLogout } from '@moodys/cre-cpm.functions.window-location';
import {
  ApiResponseFormat,
  GeneralApiProblem,
  Pagination,
  ApiResponse,
} from '../models/response';
import { Sortable } from '../models/request';

/**
 * Attempts to get a common cause of problems from an api response.
 *
 * @param response The api response.
 */
export const getGeneralApiProblem = <T>(
  response: ApiSauceResponse<T>
): GeneralApiProblem | null => {
  const issues = response.problem ? (response.data as any)?.errors : undefined;

  switch (response.problem) {
    case 'CONNECTION_ERROR':
      return { kind: 'cannot-connect', temporary: true };
    case 'NETWORK_ERROR':
      return { kind: 'cannot-connect', temporary: true };
    case 'TIMEOUT_ERROR':
      return { kind: 'timeout', temporary: true };
    case 'SERVER_ERROR':
      return { kind: 'server' };
    case 'UNKNOWN_ERROR':
      return { kind: 'unknown', temporary: true };
    case 'CLIENT_ERROR':
      switch (response.status) {
        case 401:
          return { kind: 'unauthorized' };
        case 403:
          return { kind: 'forbidden' };
        case 404:
          return { kind: 'not-found' };
        default:
          return { kind: 'rejected', issues };
      }
    default:
      return { kind: 'unknown', temporary: true };
  }
};

export interface BaseResolverResult<T> {
  data: T;
}
export interface PaginatedResolverResult<T> {
  data: T;
  pagination: Pagination;
}

export const genericProcessResponseResolver = <T>(data: T): { data: T } => ({
  data,
});

export const defaultProcessResponseResolver = <T>(
  data: ApiResponseFormat<T>
): { data: T } => ({
  data: data.data,
});

export const paginatedProcessResponseResolver = <T>(
  data: ApiResponseFormat<T>
): {
  data: T;
  pagination: Pagination;
} => ({
  data: data.data,
  pagination: data.pagination!,
});

export const emptyProcessResponseResolver = (): { data: void } => ({
  data: undefined,
});

export const processResponse = <T>(
  response: ApiSauceResponse<T, T>
): ApiResponse<T | undefined> => {
  if (!response.ok) {
    const problem = getGeneralApiProblem(response);
    if (problem) return problem;
  }

  return { data: response.data, kind: 'ok' };
};

export const processApiResponse = <T>(
  response: ApiSauceResponse<ApiResponseFormat<T>, ApiResponseFormat<T>>,
  resolver: (
    data: ApiResponseFormat<T>
  ) =>
    | { data: T }
    | { data: T; pagination: Pagination } = defaultProcessResponseResolver
): ApiResponse<T> => {
  const allowedStatuses = [201, 202, 204];

  if (!response.ok) {
    const problem = getGeneralApiProblem(response);
    if (problem) return problem;
  }

  if (!response.data && !allowedStatuses.includes(response.status)) {
    return { kind: 'bad-data' };
  }

  try {
    return { ...resolver(response.data), kind: 'ok' };
  } catch {
    return { kind: 'bad-data' };
  }
};

export const logOutSessionOnResponseError = (
  error?: AxiosError,
  forceLogout = false
): void => {
  if (forceLogout || error?.response?.status === 401) {
    redirectToLogout();
  }

  throw error;
};

export const shouldBeLoggedOut = (error: AxiosError): boolean => {
  const method = error?.config?.method;
  const url = error?.config?.url;
  const isUnauthLogoutSession =
    error?.response?.status === 401 &&
    method === 'delete' &&
    url === '/v1/auth/sessions';
  const isUnauthLoginSession =
    error?.response?.status === 401 &&
    method === 'post' &&
    url === '/v1/auth/sessions';

  return (
    error?.response?.status === 401 &&
    !isUnauthLogoutSession &&
    !isUnauthLoginSession
  );
};

export const clearEmptyValues = (
  object: object,
  allowNullCollection: string[] = []
): object => {
  const cleanObject = object;
  Object.keys(object).forEach((key) => {
    const isKeyAllowedToBeNull = allowNullCollection.includes(key);
    if (
      (!isKeyAllowedToBeNull && object[String(key)] === null) ||
      object[String(key)] === ''
    ) {
      delete cleanObject[String(key)];
    } else if (
      !isKeyAllowedToBeNull &&
      typeof object[String(key)] === 'object'
    ) {
      cleanObject[String(key)] = clearEmptyValues(
        object[String(key)],
        allowNullCollection
      );
    }
  });
  return cleanObject;
};

export const axiosSerializer = (params) =>
  qs.stringify(params, {
    encode: true,
    arrayFormat: 'brackets',
  });

export const requestAxiosInteceptor = (axiosConfig: AxiosRequestConfig) => {
  // eslint-disable-next-line no-param-reassign
  axiosConfig.paramsSerializer = axiosSerializer;

  if (axiosConfig.data) {
    if (!axiosConfig.data.disableClearEmptyValues) {
      // eslint-disable-next-line no-param-reassign
      axiosConfig.data = clearEmptyValues(
        axiosConfig.data,
        axiosConfig.data.allowNullCollection
      );
    }

    // eslint-disable-next-line no-param-reassign
    delete axiosConfig.data.allowNullCollection;
    // eslint-disable-next-line no-param-reassign
    delete axiosConfig.data.disableClearEmptyValues;
  }

  return axiosConfig;
};

export const responseAxiosInterceptor =
  (disableRedirectOnUnauthorized: boolean) => (error: AxiosError) => {
    if (!disableRedirectOnUnauthorized && shouldBeLoggedOut(error)) {
      // Error in session must logout the user always
      logOutSessionOnResponseError(error, true);
    }
    throw error;
  };

export const sortableToApiFormat = (sortable?: Sortable): string =>
  sortable && sortable.fieldOrder
    ? `${sortable.fieldOrder === 'descend' ? '-' : ''}${sortable.fieldName}`
    : '';

export const apiFormatToSortable = (apiFormatString: string): Sortable => {
  const descending = Array.from(apiFormatString)[0] === '-';
  return {
    fieldName: descending ? apiFormatString.substring(1) : apiFormatString,
    fieldOrder: descending ? 'descend' : 'ascend',
  };
};
